import React from "react";
import { CheckCircle, LinkedinLogo, UserCircle } from "@phosphor-icons/react";
import { Reveal } from "../lib/motion";
import { Eyebrow, PrimaryButton } from "../components/ui-kit";

const VALUES = [
  { t: "Ownership", d: "We treat your company as if it were our own." },
  { t: "Trust", d: "Every partnership is built on candour and confidence." },
  { t: "Execution", d: "Ideas matter only when they turn into outcomes." },
  { t: "Partnership", d: "We stand beside founders, not above them." },
  { t: "Continuous Learning", d: "We stay curious so your company stays ahead." },
  { t: "Long-term Thinking", d: "We build for endurance, not for the quarter." },
];

// Placeholder advisors — details to be updated later.
const ADVISORS = [
  {
    name: "Deepak Kumar",
    role: "Strategy · Fundraising · Finance",
    image: "/images/advisor-deepak.jpg",
    linkedin: "https://www.linkedin.com/in/deepakumr/",
    bio: "Entrepreneur and strategic advisor based in Bengaluru. Founder & CEO of SlydS and, through Gryt Capital and Craise, he has supported 2,000+ founders and businesses across $3B+ in capital raises. At Founder+, Deepak is a thinking partner — helping founders test ideas, sharpen financial clarity, strengthen investor readiness, and navigate UK and Europe markets with confidence.",
  },
  {
    name: "Abhinav Kumar",
    role: "Apparel · Manufacturing · D2C",
    image: "/images/advisor-abhinav.webp",
    linkedin: "https://www.linkedin.com/in/akabhinav123/",
    bio: "Apparel entrepreneur and manufacturing specialist with 10+ years across fashion, garment manufacturing, and custom merchandise. Founder & CEO of Dyemension AI Clothing and a NIFT Mumbai alumnus, he has worked with brands like Adidas, Puma, Tommy Hilfiger and Lenskart. At Founder+, he helps founders turn product ideas into manufacturable, priced, and scalable businesses — across D2C, B2B merchandise, operations, and factory digitization.",
  },
  {
    name: "Trina Bhattacharya",
    role: "Urban Planning · Real Estate · Execution",
    image: "/images/advisor-trina.png",
    linkedin: "https://www.linkedin.com/in/trina-bhattacharya-2153099/",
    bio: "Brings extensive experience in urban planning, real estate development, and large-scale project execution — from planning and design coordination to development management of complex projects. At Founder+, Trina advises founders on infrastructure-led opportunities, project planning, execution excellence, and strategic expansion, helping startups build structured, scalable, and future-ready growth.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="bg-cloud pt-40 pb-24 lg:pt-48 lg:pb-32">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10">
          <Reveal>
            <Eyebrow>About Founder+</Eyebrow>
            <h1 className="mt-7 max-w-4xl font-heading text-5xl font-light leading-[1.02] tracking-tight text-navy sm:text-6xl lg:text-7xl">
              Helping founders build companies that{" "}
              <span className="font-semibold text-gold">last.</span>
            </h1>
          </Reveal>
        </div>
      </section>

      {/* Story */}
      <section className="bg-white py-24 lg:py-32">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10">
          <div className="grid gap-16 lg:grid-cols-[1fr_1fr]">
            <Reveal>
              <Eyebrow>Our Story</Eyebrow>
              <h2 className="mt-6 font-heading text-3xl font-medium leading-tight tracking-tight text-navy sm:text-4xl">
                Founders deserve a partner who helps them build — not just advise.
              </h2>
            </Reveal>
            <Reveal delay={0.1} className="space-y-6 text-lg leading-relaxed text-slate-600">
              <p>
                Founder+ was created with a simple belief: founders deserve a trusted
                partner who helps them build, not just advise.
              </p>
              <p>
                Our role is to help founders create clarity, strengthen leadership,
                improve execution, and build businesses that can scale sustainably.
              </p>
              <div className="overflow-hidden rounded-3xl shadow-[0_24px_60px_rgba(11,31,58,0.14)]">
                <img
                  src="/images/collaborate.png"
                  alt="Founders collaborating in a modern workspace"
                  className="aspect-[16/10] w-full object-cover"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="bg-cloud py-24 lg:py-32">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10 grid gap-6 lg:grid-cols-2">
          <Reveal className="rounded-3xl border border-slate-100 bg-white p-10 lg:p-14">
            <Eyebrow>Mission</Eyebrow>
            <p className="mt-6 font-heading text-2xl font-light leading-snug text-navy sm:text-[1.75rem]">
              Helping founders build enduring companies through practical strategy,
              execution, leadership, and systems.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="rounded-3xl bg-navy p-10 text-white lg:p-14">
            <Eyebrow dark>Vision</Eyebrow>
            <p className="mt-6 font-heading text-2xl font-light leading-snug sm:text-[1.75rem]">
              To become the most trusted Company Building Partner for ambitious
              founders.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Core Values */}
      <section className="bg-white py-24 lg:py-32">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10">
          <Reveal className="max-w-2xl">
            <Eyebrow>Core Values</Eyebrow>
            <h2 className="mt-6 font-heading text-4xl font-medium leading-tight tracking-tight text-navy sm:text-5xl">
              The principles behind every partnership.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {VALUES.map((v, i) => (
              <Reveal
                key={v.t}
                delay={(i % 3) * 0.07}
                className="rounded-2xl border border-slate-100 bg-cloud/60 p-8 transition-colors duration-300 hover:border-teal/40"
              >
                <CheckCircle size={28} weight="duotone" className="text-teal" />
                <h3 className="mt-5 font-heading text-xl font-semibold text-navy">{v.t}</h3>
                <p className="mt-2 leading-relaxed text-slate-500">{v.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Board of Advisors */}
      <section className="bg-cloud py-24 lg:py-32" data-testid="advisors-section">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10">
          <Reveal className="max-w-2xl">
            <Eyebrow>Board of Advisors</Eyebrow>
            <h2 className="mt-6 font-heading text-4xl font-medium leading-tight tracking-tight text-navy sm:text-5xl">
              Board of Advisors - Seasoned operators behind every partnership.
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-slate-600">
              Founder+ is backed by a board of accomplished advisors — leaders across
              strategy, growth, operations, and technology who lend their experience to
              the founders we serve.
            </p>
          </Reveal>

          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {ADVISORS.map((a, i) => (
              <Reveal
                key={i}
                delay={(i % 4) * 0.07}
                data-testid={`advisor-card-${i}`}
                className="group flex flex-col rounded-3xl border border-slate-100 bg-white p-7 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_60px_rgba(11,31,58,0.10)]"
              >
                {a.image ? (
                  <div className="h-24 w-24 overflow-hidden rounded-2xl">
                    <img
                      src={a.image}
                      alt={a.name}
                      className="h-full w-full object-cover object-top"
                    />
                  </div>
                ) : (
                  <div className="grid h-20 w-20 place-items-center rounded-2xl bg-navy/5 text-navy/30">
                    <UserCircle size={44} weight="thin" />
                  </div>
                )}
                <h3 className="mt-6 font-heading text-xl font-semibold text-navy">{a.name}</h3>
                <p className="mt-1 text-sm font-semibold uppercase tracking-[0.16em] text-teal">
                  {a.role}
                </p>
                <p className="mt-4 flex-1 leading-relaxed text-slate-500">{a.bio}</p>
                {a.linkedin ? (
                  <a
                    href={a.linkedin}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`${a.name} on LinkedIn`}
                    className="mt-6 inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-navy transition-colors duration-300 hover:border-gold hover:text-gold"
                  >
                    <LinkedinLogo size={17} />
                  </a>
                ) : (
                  <span className="mt-6 inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-300">
                    <LinkedinLogo size={17} />
                  </span>
                )}
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy py-24 text-center lg:py-28">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10">
          <Reveal>
            <h2 className="mx-auto max-w-2xl font-heading text-4xl font-light tracking-tight text-white sm:text-5xl">
              Let's build something meaningful together.
            </h2>
            <div className="mt-9 flex justify-center">
              <PrimaryButton
                to="/contact"
                testid="about-cta"
                className="bg-gold text-navy hover:bg-gold-light"
              >
                Schedule a Conversation
              </PrimaryButton>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
